import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/useAuth';
import { getExecutionLogs } from '../../api/landing';
import { motion } from 'framer-motion';
import { DynamicIcon } from '../icons';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

const StatusBadge = ({ status }) => {
    const s = status.toLowerCase();
    let colorClasses = "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    let iconName = "Circle";

    if (s === 'completed' || s === 'success') {
        colorClasses = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
        iconName = "CheckCircle";
    }
    else if (s === 'failed' || s === 'error') {
        colorClasses = "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
        iconName = "AlertCircle";
    }
    else if (s === 'running') {
        colorClasses = "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
        iconName = "Loader2";
    }
    else if (s === 'waiting') {
        colorClasses = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
        iconName = "Clock";
    }

    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colorClasses}`}>
            <DynamicIcon name={iconName} className={`w-3.5 h-3.5 ${s === 'running' ? 'animate-spin' : ''}`} />
            {status}
        </span>
    );
};

import { apiFetch } from '../../api';

const LogRow = ({ log, user, token, onRefresh }) => {
    const { formatDate } = useTimezoneFormatter();
    const [expanded, setExpanded] = useState(false);

    const handleApprove = async (logId, taskId, action) => {
        try {
            // Use the actual flow_id from metadata (UUID), not the display name
            const flowId = log.metadata?.flow_id || log.flow_id || log.name.replace('Flow: ', '');
            await apiFetch(`/flows/${flowId}/runs/${logId}/approve`, token, {
                method: 'POST',
                body: JSON.stringify({ node_id: taskId, action })
            });
            // Refresh logs
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error('Failed to submit approval', err);
            alert('Failed to submit approval: ' + err.message);
        }
    };

    return (
        <>
            <motion.tr
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => setExpanded(!expanded)}
                className={`cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 ${expanded ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
            >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                    <div className="flex items-center gap-2">
                        <DynamicIcon
                            name={log.type === 'flow' ? 'Workflow' : log.type === 'dataset' ? 'Database' : 'BrainCircuit'}
                            className="w-4 h-4 text-gray-400"
                        />
                        {log.name}
                    </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {log.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={log.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(log.start_time)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.duration_sec ? `${log.duration_sec.toFixed(2)}s` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <DynamicIcon
                        name="ChevronRight"
                        className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                    />
                </td>
            </motion.tr>
            {expanded && (
                <tr className="bg-gray-50/50 dark:bg-gray-800/50">
                    <td colSpan="6" className="px-6 py-4">
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="text-sm text-gray-700 dark:text-gray-300 space-y-4"
                        >
                            {/* Pending Approvals Logic */}
                            {log.status === 'waiting' && log.metadata?.pending_tasks && log.metadata.pending_tasks.map((task, idx) => {
                                const assignee = String(task.assignee || '');

                                // Basic authorization check based on auth context
                                // e.g., 'role:Platform Admin' vs user.role
                                let isAuthorized = false;
                                if (!assignee) {
                                    isAuthorized = true; // No assignee = open for all
                                } else if (assignee.toLowerCase().startsWith('user:')) {
                                    const targetEmail = assignee.slice(5).trim().toLowerCase();
                                    if (user?.email?.toLowerCase() === targetEmail) isAuthorized = true;
                                } else if (assignee.toLowerCase().startsWith('role:')) {
                                    const targetRole = assignee.slice(5).trim().toLowerCase();
                                    const userRoleObj = user?.role || user?.roleName;
                                    const userRoleStr = typeof userRoleObj === 'string' ? userRoleObj.toLowerCase() : (userRoleObj?.name?.toLowerCase() || "");
                                    if (userRoleStr === targetRole) isAuthorized = true;
                                }

                                // Super Admins can always approve
                                const rawRole = user?.role || user?.roleName;
                                const currentRoleStr = typeof rawRole === 'string' ? rawRole.toLowerCase() : (rawRole?.name?.toLowerCase() || "");
                                if (currentRoleStr === 'super admin') {
                                    isAuthorized = true;
                                }

                                const formattedAssignee = assignee.startsWith('role:')
                                    ? `Role: ${assignee.substring(5)}`
                                    : assignee.startsWith('user:')
                                        ? `User: ${assignee.substring(5)}`
                                        : assignee || 'Anyone';

                                if (!isAuthorized) {
                                    return (
                                        <div key={idx} className="p-4 bg-amber-50/50 dark:bg-amber-900/10 rounded-xl border border-amber-200/60 dark:border-amber-800/60 shadow-sm backdrop-blur-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400">
                                                    <DynamicIcon name="Lock" className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <h4 className="text-amber-800 dark:text-amber-300 font-semibold mb-0.5 mt-0.5">Approval Pending</h4>
                                                    <p className="text-sm text-amber-700/80 dark:text-amber-400/80 m-0">
                                                        Waiting for <span className="font-bold">{formattedAssignee}</span> to review and approve.
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 rounded-md text-xs font-semibold text-amber-700 dark:text-amber-400 shadow-sm whitespace-nowrap flex items-center gap-1.5 self-start md:self-auto">
                                                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Blocked
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={idx} className="p-5 bg-white dark:bg-gray-800 rounded-xl border border-amber-300 dark:border-amber-600 shadow-md">
                                        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-100 dark:border-gray-700">
                                            <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg text-amber-600 dark:text-amber-400 shadow-inner">
                                                <DynamicIcon name="UserCheck" className="w-5 h-5 shrink-0" />
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">Your Approval is Required</h4>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 m-0">You are assigned as <span className="font-semibold">{formattedAssignee}</span> to complete this task.</p>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-100 dark:border-gray-800 mb-5 relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-1 h-full bg-amber-400"></div>
                                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed m-0 font-medium">
                                                {task.description || "Please review the data and approve or reject."}
                                            </p>
                                        </div>

                                        <div className="flex gap-3 justify-end items-center">
                                            <button
                                                onClick={() => handleApprove(log.id, task.id, 'reject')}
                                                className="py-2 px-5 text-sm font-semibold bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-2 shadow-sm">
                                                <DynamicIcon name="X" className="w-4 h-4" /> Reject
                                            </button>
                                            <button
                                                onClick={() => handleApprove(log.id, task.id, 'approve')}
                                                className="py-2 px-6 text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg transition-all flex items-center gap-2 shadow hover:shadow-lg transform hover:-translate-y-0.5 border border-amber-600">
                                                <DynamicIcon name="Check" className="w-4 h-4" /> Approve Task
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">User</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                            {(log.user || 'S').charAt(0).toUpperCase()}
                                        </div>
                                        {log.user || 'System'}
                                    </div>
                                </div>
                                {log.metadata?.flow_id && (
                                    <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Flow / App ID</span>
                                        <span className="font-mono text-xs">{log.metadata.flow_id}</span>
                                    </div>
                                )}
                                <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Log ID</span>
                                    <span className="font-mono text-xs">{log.id}</span>
                                </div>
                                <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-1">Duration</span>
                                    <span className="font-mono">{log.duration_sec ? `${log.duration_sec.toFixed(4)}s` : 'N/A'}</span>
                                </div>
                            </div>

                            {log.error_message && (
                                <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 rounded border border-red-200 dark:border-red-800 flex gap-3 items-start">
                                    <DynamicIcon name="AlertTriangle" className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <span className="font-bold block mb-1">Error Details</span>
                                        <p className="whitespace-pre-wrap font-mono text-xs">{log.error_message}</p>
                                    </div>
                                </div>
                            )}

                            {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div>
                                    <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">Metadata</span>
                                    <div className="bg-gray-900 text-gray-200 p-4 rounded-lg overflow-auto max-h-60 font-mono text-xs shadow-inner">
                                        <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default function ExecutionLogViewer() {
    const { token, user } = useAuth(); // <- Inject user 
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const fetchLogs = () => {
        setLoading(true);
        const params = {};
        if (typeFilter) params.type = typeFilter;
        if (statusFilter) params.status = statusFilter;

        getExecutionLogs(token, params)
            .then(data => {
                setLogs(data || []);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed logs", err);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (token) fetchLogs();
    }, [token, typeFilter, statusFilter]);

    return (
        <div className="space-y-8 p-1">
            {/* Header */}
            <header>
                <motion.h1
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-3xl font-bold text-gray-900 dark:text-white"
                >
                    Execution Logs
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-gray-500 dark:text-gray-400 mt-2"
                >
                    Monitor and debug system activities, workflows, and data processing.
                </motion.p>
            </header>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden flex flex-col h-[700px]"
            >
                {/* Filters Toolbar */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 items-center bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <DynamicIcon name="Filter" className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
                    </div>

                    <select
                        className="form-select text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white py-1.5"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        <option value="flow">Flows</option>
                        <option value="dataset">Datasets</option>
                        <option value="genai">GenAI</option>
                        <option value="model">ML Models</option>
                    </select>

                    <select
                        className="form-select text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white py-1.5"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="running">Running</option>
                        <option value="waiting">Waiting</option>
                    </select>

                    <button
                        onClick={fetchLogs}
                        className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 transition-colors"
                    >
                        <DynamicIcon name="RefreshCcw" className="w-3.5 h-3.5" />
                        Refresh
                    </button>
                </div>

                {/* Table Content */}
                <div className="flex-1 overflow-auto custom-scrollbar relative">
                    {loading && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center z-10 backdrop-blur-sm">
                            <div className="flex flex-col items-center gap-2 text-gray-500">
                                <DynamicIcon name="Loader2" className="w-8 h-8 animate-spin text-blue-500" />
                                <span className="text-sm">Fetching logs...</span>
                            </div>
                        </div>
                    )}

                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-0 shadow-sm">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Item
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Started
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Duration
                                </th>
                                <th scope="col" className="relative px-6 py-3">
                                    <span className="sr-only">Expand</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {!loading && logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <DynamicIcon name="SearchX" className="w-12 h-12 mb-3 opacity-20" />
                                            <p className="text-base font-medium text-gray-900 dark:text-gray-200">No logs found</p>
                                            <p className="text-sm mt-1">Try adjusting your filters or check back later.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => <LogRow key={log.id} log={log} user={user} token={token} onRefresh={fetchLogs} />)
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer / Pagination (Placeholder) */}
                <div className="p-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs text-center text-gray-400">
                    Showing {logs.length} most recent logs
                </div>
            </motion.div>
        </div>
    );
}
